import { EventType } from "@/analytics/analytics-events";
import {
  CustomSearchParams,
  DeepLink,
  encodeDeepLink,
} from "@/components/common/deep-link/deep-link-encoding";
import { DecodedDeepLinkData } from "@/data-preparation-tool/deep-link/deep-link-data-decoded";
import { encodeDeepLinkData } from "@/data-preparation-tool/deep-link/deep-link-encode";
import { runtimeConfig } from "@/runtime-config";
import { parseFeaturesAndValuesFromUrl } from "@/store/features/features";
import { store } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import {
  selectCompanyId,
  selectDashboardUrl,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { GUID } from "@faro-lotv/ielement-types";
import { pickBy } from "es-toolkit";
import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

type RedirectToRegistrationToolArgs = {
  /** Id of the project we want to do the manual alignment/registration for */
  projectId: GUID;
  /** Id of the Reference PointCloud*/
  pointCloudId1: GUID;
  /** Id of the model PointCloud*/
  pointCloudId2: GUID;
};

type RedirectToMultiRegistrationToolArgs = {
  /** Id of the project we want to see the registration result for */
  projectId: GUID;
  /** Id of the Dataset containing the registered point clouds */
  datasetId: GUID;
  /** The ID of the completed registration task to display the results of. */
  registrationTaskId?: GUID;
};

export type DraftViewArgs = {
  /** Enable the handling of developer features. */
  hasDev?: boolean;
};

export type DraftViewOpenFrom =
  | "draftImportScansDialog"
  | "editScans"
  | "importDronesDialog"
  | "importScansDialog"
  | "importDronesEmpty"
  | "importScansEmpty"
  | "mergeConflict"
  | "pendingChanges"
  | "publishRevision"
  | "refineRegistration"
  | "registrationFinished"
  | "restartRegistration"
  | "toggleButton";

/**
 * Function to navigate to the Draft View page.
 *
 * @param projectId Project ID.
 * @param from Source from which the Draft View page is opened.
 * @param options Options provided as URL flags to the Draft View page.
 */
type FunctionNavigateToDraftView = (
  projectId: GUID,
  from: DraftViewOpenFrom,
  options?: DraftViewArgs,
) => void;

/**
 * Redirect the user to the feedback form
 */
export function redirectToGetEarlyAccessForm(): void {
  if (runtimeConfig.externalLinks.getEarlyAccessForm) {
    window.open(runtimeConfig.externalLinks.getEarlyAccessForm, "_blank");
  }
}

/** Keeps the WebEditor from redirecting a migrated project straight back to the viewer */
const WEB_EDITOR_FORCE_PARAM = "force";

/** Hash route of the WebEditor opening a project, e.g. /#editor/<projectId> */
const EDITOR_ROUTE = "editor";

/**
 * Route of the EntryPage that takes care of translating a Viewer deep link into the
 * corresponding WebEditor deep link that keeps the same view.
 */
const ENTRY_PAGE_REDIRECT_ROUTE = "viewer-link";

/**
 * @returns the WebEditor url opening a project
 * @param projectId of the project we want to open in the WebEditor
 */
export function generateWebEditorUrl(projectId: GUID): URL {
  // The WebEditor routes from its root; the /app entry point of the config redirects back here
  const url = new URL("/", runtimeConfig.externalLinks.webEditor);

  url.hash = `${EDITOR_ROUTE}/${projectId}?${WEB_EDITOR_FORCE_PARAM}=true`;

  return url;
}

/**
 * @returns the EntryPage URL that will open the WebEditor on the same view as the one in the Viewer
 * @param viewerUrl The current Viewer deep link.
 */
export function generateEntryPageRedirectUrl({ pathname, search }: URL): URL {
  return new URL(
    `${ENTRY_PAGE_REDIRECT_ROUTE}${pathname}${search}`,
    runtimeConfig.authentication.ulmUrl,
  );
}

/**
 * Redirect the user to the WebEditor page for the current project
 *
 * @param projectId of the project we want to open in the WebEditor
 */
export function redirectToWebEditor(projectId: GUID): void {
  window.location.href = generateWebEditorUrl(projectId).toString();
}

/**
 * Redirects to the projects details page, or one of it's tabs, in the Dashboard.
 *
 * @param projectId Id of the project that is currently open
 * @param subPage Optional redirect target within the Dashboard's project page.
 *  Can also include feature flags. (e.g. "data" or "data?enableBeta=true).
 *  If not used or empty, it defaults to "details", which is the project main page.
 * @param dashboardUrl Optional URL to the Dashboard, as it can be obtained by selectDashboardUrl.
 *  If not provided, this will fallback to the one from the runtime config.
 */
export function redirectToDashboardProjectPage(
  projectId: GUID,
  subPage?: string,
  dashboardUrl: string = runtimeConfig.externalLinks.dashboardUrl,
): void {
  assert(projectId, "Need a non empty projectId to redirect");

  // workaround for a bug in the dashboard, see https://faro01.atlassian.net/browse/ST-1729
  if (!subPage) {
    subPage = "details";
  }
  window.location.href = `${dashboardUrl}/projects/${projectId}/${subPage}`;
}

/**
 * Redirects to the Dashboard's project overview page, or one of it's sub pages.
 *
 * @param subPage Optional redirect target within the Dashboard's projects page.
 *  Can also include feature flags. (e.g. "archived" or "archived?enableBeta=true).
 *  If not used or empty, it defaults to "active", which is the projects main page.
 * @param dashboardUrl Optional URL to the Dashboard, as it can be obtained by selectDashboardUrl.
 *  If not provided, this will fallback to the one from the runtime config.
 */
export function redirectToDashboardProjectsPage(
  subPage?: string,
  dashboardUrl: string = runtimeConfig.externalLinks.dashboardUrl,
): void {
  if (!subPage) {
    subPage = "active";
  }
  window.location.href = `${dashboardUrl}/projects/${subPage}`;
}

/**
 * Redirects to the (most fitting) Dashboard for the user
 */
export function redirectToDashboard(): void {
  // Check if the store has a Dashboard stored
  const dashboardUrl =
    selectDashboardUrl(store.getState()) ??
    runtimeConfig.externalLinks.dashboardUrl;

  window.location.href = dashboardUrl;
}

/** @returns the url to use to open the dashboard addon settings page if available */
export function useDashboardAddOnUrl(): string | undefined {
  const companyId = useAppSelector(selectCompanyId);

  return useMemo(() => {
    if (!runtimeConfig.externalLinks.dashboardUrl || !companyId) return;
    return new URL(
      `${companyId}/settings/addOns-and-services`,
      runtimeConfig.externalLinks.dashboardUrl,
    ).toString();
  }, [companyId]);
}

/** @returns the url to open the account page for the current user (opens the Account & Security tab in Dashboard v2)*/
export function useDashboardAccountUrl(): string | undefined {
  const companyId = useAppSelector(selectCompanyId);
  const dashboardUrl =
    useAppSelector(selectDashboardUrl) ??
    runtimeConfig.externalLinks.dashboardUrl;

  return useMemo(() => {
    if (!dashboardUrl || !companyId) {
      return;
    }
    return new URL(
      // This is a Dashboard v1 route that is forwarded to the Account & Security page in Dashboard v2
      `${companyId}/me`,
      dashboardUrl,
    ).toString();
  }, [companyId, dashboardUrl]);
}

/**
 * @returns the url to open the account language & unit tab on the Dashboard v2 page for the current user
 */
export function useDashboardLanguageUnitUrl(): string | undefined {
  const dashboardUrl =
    useAppSelector(selectDashboardUrl) ??
    runtimeConfig.externalLinks.dashboardUrl;

  return useMemo(() => {
    if (!dashboardUrl) {
      return;
    }
    return new URL("account/preferences", dashboardUrl).toString();
  }, [dashboardUrl]);
}

/**
 * @param companyId The ID of the company for which we want the integrations URL
 * @param projectId The ID of the project for which we want the integrations URL
 * @returns the url to open the project integrations tab on Dashboard V2 page for the current user
 */
export function useDashboardProjectIntegrationsUrl(
  companyId: GUID | undefined,
  projectId: GUID | undefined,
): string | undefined {
  const dashboardUrl =
    useAppSelector(selectDashboardUrl) ??
    runtimeConfig.externalLinks.dashboardUrl;

  return useMemo(() => {
    if (!dashboardUrl || !companyId || !projectId) {
      return;
    }
    return new URL(
      `${companyId}/projects/${projectId}/integrations?enableBeta=true`,
      dashboardUrl,
    ).toString();
  }, [companyId, projectId, dashboardUrl]);
}

/**
 * @returns the url for the dashboard subscription page
 * @param dashboardUrl Base dashboard URL
 * @param companyId The ID of the company
 */
export function createDashboardSubscriptionUrl(
  dashboardUrl: string,
  companyId: GUID,
): string {
  return new URL(`${companyId}/settings/subscription`, dashboardUrl).toString();
}

/**
 * @param options Options provided as URL flags to the Draft View page.
 * @returns URL flags supported by the Draft View page, based on the provided options.
 */
function getUrlFlagsForDraftView(options?: DraftViewArgs): string {
  const params = new URLSearchParams();
  if (options?.hasDev) {
    params.append("enableDev", "true");
  }
  addEnabledFeatureFlags(params);
  return params.size ? `?${params.toString()}` : "";
}

/**
 * @param projectId Project ID.
 * @param options Options provided as URL flags to the Draft View page.
 * @returns Route to the Draft View page, to be used by navigate() or <NavLink>.
 */
export function getDraftViewRoute(
  projectId: GUID,
  options?: DraftViewArgs,
): string {
  const flags = getUrlFlagsForDraftView(options);
  return `/project/${projectId}/draft${flags}`;
}

/**
 * @param projectId Project ID.
 * @param options Options provided as URL flags to the Draft View page.
 *        These are used here as well to retain these flags when navigating back to the Live view.
 * @returns Route to the Live view, to be used by navigate() or <NavLink>.
 */
export function getLiveViewRoute(
  projectId: GUID,
  options?: DraftViewArgs,
): string {
  const flags = getUrlFlagsForDraftView(options);
  return `/project/${projectId}${flags}`;
}

/** @returns A function to navigate to the Draft View page. */
export function useNavigateToDraftView(): FunctionNavigateToDraftView {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasDev = searchParams.has(CustomSearchParams.enableDev);

  function navigateToDraftView(
    projectId: GUID,
    from: DraftViewOpenFrom,
    options?: DraftViewArgs,
  ): void {
    if (!options) {
      options = {};
    }
    options.hasDev = hasDev;

    Analytics.track(EventType.openDraftView, { from });
    const route = getDraftViewRoute(projectId, options);
    navigate(route);
  }

  return navigateToDraftView;
}

/** @returns A function to navigate to the live view page. */
export function useNavigateToLiveView(): (projectId: GUID) => void {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasDev = searchParams.has(CustomSearchParams.enableDev);

  function navigateToLiveView(projectId: GUID): void {
    const options: DraftViewArgs = {};
    options.hasDev = hasDev;

    const route = getLiveViewRoute(projectId, options);
    navigate(route);
  }

  return navigateToLiveView;
}

/**
 * Redirect to the manual alignment/registration tool deployed in HoloBuilder for the selected project
 */
export function redirectToRegistrationTool({
  projectId,
  pointCloudId1,
  pointCloudId2,
}: RedirectToRegistrationToolArgs): void {
  assert(projectId !== "", "Need a non empty projectId to redirect");

  const params = new URLSearchParams({
    refCloudId: pointCloudId1,
    modelCloudId: pointCloudId2,
  });

  redirectWithFeatureFlags(`/manual-alignment/project/${projectId}`, params);
}

type RedirectToDataPreparationPageArgs = {
  /** The ID of the project containing the data. */
  projectId: GUID;
  /**
   * The ID of the revision to inspect or edit.
   * If not provided, the latest draft revision will be used.
   */
  revisionId?: GUID;
  /** Link to a specific view state. */
  deepLinkData?: DecodedDeepLinkData;
};

/**
 * @returns the URL to the data preparation page.
 *  Includes currently active feature flags.
 */
function getDataPreparationUrl({
  projectId,
  revisionId,
  deepLinkData = {},
}: RedirectToDataPreparationPageArgs): string {
  assert(projectId !== "", "Need a non empty projectId to redirect");

  const encodedDeepLinkData = encodeDeepLinkData(deepLinkData);

  const params = new URLSearchParams(
    // Remove undefined values to prevent invalid URL parameters
    pickBy(
      {
        ...encodedDeepLinkData,
        revisionId,
      },
      (value) => value !== undefined,
    ),
  );

  return getUrlWithFeatureFlags(`/project/${projectId}/draft`, params);
}

/**
 * @returns A function to navigate to the data preparation page.
 *  Retains currently active feature flags and avoids a full page reload.
 */
export function useNavigateToDataPreparationPage(): (
  args: RedirectToDataPreparationPageArgs,
) => void {
  const navigate = useNavigate();

  return useCallback(
    (args: RedirectToDataPreparationPageArgs) =>
      navigate(getDataPreparationUrl(args)),
    [navigate],
  );
}

/**
 * Redirect to the manual alignment/registration tool deployed in HoloBuilder for the selected project
 */
export function redirectToMultiRegistrationTool({
  projectId,
  datasetId,
  registrationTaskId,
}: RedirectToMultiRegistrationToolArgs): void {
  assert(projectId, "Need a non empty projectId to redirect");

  const params = new URLSearchParams();
  params.append("datasetId", datasetId);
  if (registrationTaskId) {
    params.append("taskId", registrationTaskId);
  }

  redirectWithFeatureFlags(
    `/multi-registration-result/project/${projectId}`,
    params,
  );
}

/**
 * Redirect the user to the Sphere Viewer page for the current project
 *
 * @param projectId of the project we want to open in the Sphere Viewer
 * @param state optional information to populate deepLink to correctly restore view
 */
export function redirectToViewer(projectId: GUID, state?: DeepLink): void {
  const params = new URLSearchParams();

  // This will populate the searchParameters of the url with all the data required to generate a deep link
  if (state) {
    encodeDeepLink(state, params);
  }

  redirectWithFeatureFlags(`/project/${projectId}`, params);
}

/**
 * @param projectId of the project we want to open in the Sphere Viewer
 * @param state optional information to populate deepLink to correctly restore view
 * @returns relative URL to the Sphere Viewer page for the current project
 */
export function generateViewerUrl(projectId: GUID, state?: DeepLink): string {
  const path = `/project/${projectId}`;
  const params = new URLSearchParams();

  // This will populate the searchParameters of the url with all the data required to generate a deep link
  if (state) {
    encodeDeepLink(state, params);
  }

  // Retain the enabled feature flags during the redirect
  addEnabledFeatureFlags(params);

  // Add the search parameters to the URL, if there are any
  return params.size ? `${path}?${params.toString()}` : path;
}

/**
 * @returns URL object to the Sphere Viewer page for the current project
 * @param projectId of the project we want to open in the Sphere Viewer
 * @param state optional information to populate deepLink to correctly restore view
 */
export function generateFullViewerUrl(projectId: GUID, state?: DeepLink): URL {
  return new URL(generateViewerUrl(projectId, state), window.location.origin);
}

/**
 * @returns A URl that opens the email app with prefilled content.
 * @param email The email address to send the mail to.
 * @param subject The subject line of the email.
 * @param body The content of the email.
 */
export function createMailToLink(
  email: string,
  subject: string,
  body: string,
): string {
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * @param path The path of the URL.
 * @param params The search params to add to the URL.
 * @returns The URL with the given search params, plus the currently active feature flags.
 */
function getUrlWithFeatureFlags(
  path: string,
  params?: URLSearchParams,
): string {
  const featureParams = params ?? new URLSearchParams();
  // Retain the enabled feature flags during the redirect
  addEnabledFeatureFlags(featureParams);

  // Add the search parameters to the URL, if there are any
  return featureParams.size ? `${path}?${featureParams.toString()}` : path;
}

/** @param url the URL to redirect to. */
function redirect(url: string): void {
  window.location.assign(url);
}

/**
 * Redirect to the given path and add the params and selected feature flags to the URL.
 *
 * @param path The path to redirect to, contrary to `URL`, this can be a relative path.
 * @param params Search parameters to add to the URL.
 */
function redirectWithFeatureFlags(
  path: string,
  params?: URLSearchParams,
): void {
  redirect(getUrlWithFeatureFlags(path, params));
}

/**
 * @param searchParams The URL search params to add the currently enabled feature flags to.
 */
function addEnabledFeatureFlags(searchParams: URLSearchParams): void {
  const currentUrl = new URL(window.location.href);
  const featureQueries = parseFeaturesAndValuesFromUrl(currentUrl.searchParams);

  for (const query of featureQueries) {
    if (query.disabled) {
      searchParams.set(query.featureFlag, "false");
      continue;
    }

    if (query.value === null) {
      searchParams.set(query.featureFlag, "");
      continue;
    }

    searchParams.set(query.featureFlag, query.value);
  }
}
